import { P2GAlert, P2GBox, P2GIcon } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { lazy, useMemo } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { EditType } from './EditType';
import { ErrorCodeIcon } from './ErrorCodeIcon';
import { ErrorCodeIconSrc } from './ErrorCodeIconSrc';
import { ErrorCodeMessage } from './ErrorCodeMessage';
import { ErrorCodeResolutionsByErrors } from './ErrorCodeResolutions';

import ErrorSummary from './ErrorSummary';
import QuickFixWrapper from '../edit/QuickFixWrapper';
import { QuoteErrorIsCovered, QuoteErrorResolutions, ResolveQuoteErrors } from './QuoteErrors';
import { onlyUnique } from '../../../lib/helpers/ArrayHelper';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

const ServiceInformationModal = lazy(() => import('./ServiceInformationModal'));

/** The row shows this many sentences and this many fix links. */
const MaxRowErrors = 3;

interface props {
  item: SmartSendItemApiModel;
  variant: 'body2';
  edit: (type: EditType) => void;
  xs?: boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  sm?: boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  md?: boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  lg?: boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  xl?: boolean | 'auto' | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  component: 'Grid' | 'Box';
  children: React.ReactNode;
}

const ItemErrorSummary: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const itemErrors = useMemo(
    () => ErrorCodeResolutionsByErrors(props.item?.State?.validation?.Errors),
    [props.item?.State?.validation]
  );

  // Resolved to their copy here rather than at render, so duplicates can be collapsed on the sentence the
  // customer actually reads. Capped at the same 3 as the fix links below, so a consignment raising one
  // issue per parcel cannot grow the alert without bound.
  const quoteErrors = useMemo(
    () => ResolveQuoteErrors(t, props.item?.State?.validation?.QuoteState?.Errors, MaxRowErrors),
    [t, props.item?.State?.validation]
  );

  // Still gated on OUR errors alone: an item that validated and can be booked must not grow a red alert
  // because quoting attached an issue to the quote it did return.
  if (!itemErrors || itemErrors.length === 0) {
    return <>{props.children}</>;
  }

  const mainError = itemErrors[0];

  // SERVICE-UNAVAILABLE says we could not price the shipment without saying why, so where quoting told us
  // why it takes the slot. PostQuoteValidation short-circuits on anything it found itself, so that code
  // only reaches this row when we found nothing more specific to say. Our own codes keep the slot.
  const headlineError = mainError === 'SERVICE-UNAVAILABLE' ? quoteErrors[0] : undefined;

  // A restatement of the field the headline already names is dropped rather than listed beneath. A no-op
  // when quoting owns the headline, since SERVICE-UNAVAILABLE names no field.
  const remainingErrors = (headlineError ? quoteErrors.slice(1) : quoteErrors).filter(
    (r) => !QuoteErrorIsCovered(r.code, mainError)
  );

  // These REPLACE the links SERVICE-UNAVAILABLE offers rather than joining them: "Change Service" and
  // "Change Parcel Dimensions" exist because we did not know why nothing priced, and once quoting has said
  // it is the recipient's postcode they are noise. A fault we cannot place yields nothing, leaving the row
  // the generic pair - still the best we can say there.
  const quoteResolutions = headlineError
    ? quoteErrors.flatMap((r) => QuoteErrorResolutions(r.code, r.subCode)).filter(onlyUnique)
    : [];

  const summaryErrors = quoteResolutions.length > 0 ? quoteResolutions : itemErrors;
  const icon = { icon: <ErrorCodeIcon error={mainError} />, path: ErrorCodeIconSrc(mainError) };

  const alert = (
    <P2GAlert
      type="error"
      icon={
        icon?.icon && (
          <P2GIcon palette="error" size="md">
            {icon.icon}
          </P2GIcon>
        )
      }
    >
      <P2GBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <P2GText variant={props.variant}>
          <strong>{headlineError ? headlineError.message : <ErrorCodeMessage error={mainError} />}</strong>
        </P2GText>
        {remainingErrors.map((r) => (
          <P2GText key={r.message} variant="caption">
            {r.message}
          </P2GText>
        ))}
      </P2GBox>
      <P2GBox mt={1}>
        <QuickFixWrapper error={mainError} item={props.item}>
          <ErrorSummary
            limit={MaxRowErrors}
            errors={summaryErrors}
            edit={props.edit}
            controls={{
              'SERVICE-UNAVAILABLE': (
                <ServiceInformationModal
                  itemId={props.item.Id}
                  service={props.item.Service}
                  dims={props.item.PackageDimensions}
                  senderIsoCode={props.item.Sender.CountryIsoCode}
                  senderSubdivision={props.item.Sender.CountrySubdivision}
                  recipientIsoCode={props.item.Recipient.CountryIsoCode}
                  recipientSubdivision={props.item.Recipient.CountrySubdivision}
                  currencyCode={props.item.CurrencyCode}
                />
              )
            }}
          />
        </QuickFixWrapper>
      </P2GBox>
    </P2GAlert>
  );

  return props.component == 'Grid' ? (
    <P2GGrid item sm={props.sm} md={props.md} xs={props.xs} lg={props.lg} xl={props.xl}>
      {alert}
    </P2GGrid>
  ) : (
    alert
  );
};
export default ItemErrorSummary;
